import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";

const BUCKET = "product-images";
const GREEN = "#3a7d44";

// ──────────────────────────────────────────────────────────────────────────
// SMALL SHARED UI
// ──────────────────────────────────────────────────────────────────────────
function Btn({ children, onClick, variant = "primary", disabled, style }) {
  const base = {
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontWeight: 600,
    fontSize: 13,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
  const variants = {
    primary: { background: GREEN, color: "#fff" },
    danger: { background: "#dc2626", color: "#fff" },
    ghost: { background: "#f3f4f6", color: "#374151" },
    outline: { background: "#fff", color: GREEN, border: `1px solid ${GREEN}` },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

function Modal({ title, onClose, children, width = 480 }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 24, width, maxHeight: "85vh", overflowY: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #e5e7eb",
  fontSize: 14, outline: "none", boxSizing: "border-box",
};

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function StatusBadge({ status }) {
  const colors = {
    available: { bg: "#dcfce7", fg: "#166534" },
    sold: { bg: "#fee2e2", fg: "#991b1b" },
    sold_out: { bg: "#fee2e2", fg: "#991b1b" },
    returned: { bg: "#fef3c7", fg: "#92400e" },
  };
  const c = colors[status] || { bg: "#f3f4f6", fg: "#374151" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 50,
      fontSize: 11, fontWeight: 700, textTransform: "capitalize",
      background: c.bg, color: c.fg,
    }}>
      {status || "unknown"}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// LOGIN
// ──────────────────────────────────────────────────────────────────────────
function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) setError(error.message);
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb" }}>
      <form onSubmit={handleLogin} style={{ background: "#fff", padding: 32, borderRadius: 16, width: 340, boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <h2 style={{ margin: "0 0 4px", color: "#1a2e1a" }}>Admin sign in</h2>
        <p style={{ margin: "0 0 20px", color: "#9ca3af", fontSize: 13 }}>Ritu Fashion Store — Sales dashboard</p>
        <Field label="Email">
          <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Field label="Password">
          <input style={inputStyle} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        {error && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <Btn style={{ width: "100%" }} disabled={loading}>{loading ? "Signing in…" : "Sign in"}</Btn>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// TAB 1 — UNMAPPED BARCODES
// Every stock row your POS created that isn't yet linked to a website product.
// ──────────────────────────────────────────────────────────────────────────
function UnmappedBarcodesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [products, setProducts] = useState([]);
  const [attachTarget, setAttachTarget] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [barcodeFilter, setBarcodeFilter] = useState("");
  const [barcodeFilterDebounced, setBarcodeFilterDebounced] = useState("");

  // Wait for the person to stop typing before hitting the DB — avoids a
  // query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setBarcodeFilterDebounced(barcodeFilter.trim()), 400);
    return () => clearTimeout(t);
  }, [barcodeFilter]);

  const loadUnmapped = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("Stock")
      .select("stock_id, barcode_id, category, subcategory, price, quantity, status, size_label")
      .is("product_id", null)
      .order("created_at", { ascending: false });

    if (statusFilter !== "all") query = query.eq("status", statusFilter);
    if (barcodeFilterDebounced) query = query.ilike("barcode_id", `%${barcodeFilterDebounced}%`);

    const { data, error } = await query;
    if (error) console.error(error);
    setRows(data || []);
    setLoading(false);
  }, [statusFilter, barcodeFilterDebounced]);

  const loadProducts = useCallback(async () => {
    const { data } = await supabase.from("products").select("product_id, name").order("name");
    setProducts(data || []);
  }, []);

  useEffect(() => { loadUnmapped(); }, [loadUnmapped]);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  function toggleRow(stockId) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(stockId) ? next.delete(stockId) : next.add(stockId);
      return next;
    });
  }

  async function attachToExisting() {
    if (!attachTarget || selected.size === 0) return;
    // online_price varies per row (each barcode's own offline price), so a
    // single bulk update can't set it — update each selected row
    // individually, prefilled from its real price + a static 10% off.
    const updates = Array.from(selected).map((stockId) => {
      const row = rows.find((r) => r.stock_id === stockId);
      return supabase
        .from("Stock")
        .update({ product_id: attachTarget, online_price: row?.price ?? null, online_discount: 10 })
        .eq("stock_id", stockId);
    });
    const results = await Promise.all(updates);
    const failed = results.find((r) => r.error);
    if (failed) return alert(failed.error.message);
    setSelected(new Set());
    setAttachTarget("");
    loadUnmapped();
  }

  async function saveSizeLabel(stockId, value) {
    await supabase.from("Stock").update({ size_label: value }).eq("stock_id", stockId);
  }

  async function createProductAndAttach(form) {
    const baseSlug = slugify(form.name);
    const uniqueSlug = `${baseSlug}-${Math.random().toString(36).slice(2, 6)}`;
    const { data, error } = await supabase
      .from("products")
      .insert({
        name: form.name,
        slug: uniqueSlug,
        description: form.description,
        gender: form.gender || null,
        category: form.category,
        subcategory: form.subcategory,
        brand: form.brand,
        is_online_active: false,
      })
      .select()
      .single();
    if (error) return alert(error.message);
    if (selected.size > 0) {
      const updates = Array.from(selected).map((stockId) => {
        const row = rows.find((r) => r.stock_id === stockId);
        return supabase
          .from("Stock")
          .update({ product_id: data.product_id, online_price: row?.price ?? null, online_discount: 10 })
          .eq("stock_id", stockId);
      });
      await Promise.all(updates);
    }
    setSelected(new Set());
    setShowCreate(false);
    loadUnmapped();
    loadProducts();
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={barcodeFilter}
            onChange={(e) => setBarcodeFilter(e.target.value)}
            placeholder="Search barcode…"
            style={{ ...inputStyle, width: 160 }}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...inputStyle, width: 150 }}>
            <option value="all">All statuses</option>
            <option value="available">Available</option>
            <option value="sold">Sold</option>
            <option value="sold_out">Sold out</option>
            <option value="returned">Returned</option>
          </select>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            {loading ? "Loading…" : `${rows.length} barcode(s)`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select
            value={attachTarget}
            onChange={(e) => setAttachTarget(e.target.value)}
            style={{ ...inputStyle, width: 220 }}
          >
            <option value="">Attach selected to…</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>{p.name}</option>
            ))}
          </select>
          <Btn variant="outline" disabled={!attachTarget || selected.size === 0} onClick={attachToExisting}>
            Attach ({selected.size})
          </Btn>
          <Btn onClick={() => setShowCreate(true)}>+ New product from selected</Btn>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #f0f0f0" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f9fafb", textAlign: "left" }}>
              <th style={{ padding: 10, width: 32 }}></th>
              <th style={{ padding: 10 }}>Barcode</th>
              <th style={{ padding: 10 }}>Status</th>
              <th style={{ padding: 10 }}>Category</th>
              <th style={{ padding: 10 }}>Subcategory</th>
              <th style={{ padding: 10 }}>Price</th>
              <th style={{ padding: 10 }}>Size label</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.stock_id} style={{ borderTop: "1px solid #f3f4f6" }}>
                <td style={{ padding: 10 }}>
                  <input type="checkbox" checked={selected.has(r.stock_id)} onChange={() => toggleRow(r.stock_id)} />
                </td>
                <td style={{ padding: 10 }}>{r.barcode_id}</td>
                <td style={{ padding: 10 }}><StatusBadge status={r.status} /></td>
                <td style={{ padding: 10 }}>{r.category}</td>
                <td style={{ padding: 10 }}>{r.subcategory}</td>
                <td style={{ padding: 10 }}>₹{r.price}</td>
                <td style={{ padding: 10 }}>
                  <input
                    defaultValue={r.size_label || ""}
                    placeholder="e.g. 4-5 Years"
                    onBlur={(e) => saveSizeLabel(r.stock_id, e.target.value)}
                    style={{ ...inputStyle, padding: "5px 8px", fontSize: 12 }}
                  />
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>
                {statusFilter === "all" && !barcodeFilterDebounced ? "All barcodes are mapped." : "No barcodes match this filter."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateProductModal onClose={() => setShowCreate(false)} onCreate={createProductAndAttach} selectedCount={selected.size} />
      )}
    </div>
  );
}

function CreateProductModal({ onClose, onCreate, selectedCount }) {
  const [form, setForm] = useState({ name: "", description: "", gender: "", category: "", subcategory: "", brand: "" });
  return (
    <Modal title="Create new product" onClose={onClose}>
      <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0 }}>
        {selectedCount} selected barcode(s) will be linked to this product once created.
      </p>
      <Field label="Product name">
        <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Floral Party Frock" />
      </Field>
      <Field label="Description">
        <textarea style={{ ...inputStyle, minHeight: 70 }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>
      <Field label="Gender">
        <select style={inputStyle} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
          <option value="">Select gender…</option>
          <option value="girls">Girls</option>
          <option value="boys">Boys</option>
          <option value="unisex">Unisex</option>
        </select>
      </Field>
      <Field label="Category">
        <input style={inputStyle} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Casual, Ethnic Wear, Party Frocks" />
      </Field>
      <Field label="Subcategory (optional)">
        <input style={inputStyle} value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="e.g. Sharara, Lehenga, Salwar Suit" />
      </Field>
      <Field label="Brand">
        <input style={inputStyle} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
      </Field>
      <Btn style={{ width: "100%" }} disabled={!form.name} onClick={() => onCreate(form)}>Create & attach</Btn>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// TAB 2 — PRODUCTS (details, sizes, images — only upload what you're listing)
// ──────────────────────────────────────────────────────────────────────────
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from("products").select("*").order("updated_at", { ascending: false });
    setProducts(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: "flex", gap: 24 }}>
      <div style={{ flex: "0 0 260px", background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", padding: 8, maxHeight: "75vh", overflowY: "auto" }}>
        {products.map((p) => (
          <button
            key={p.product_id}
            onClick={() => setSelectedId(p.product_id)}
            style={{
              display: "block", width: "100%", textAlign: "left", padding: "10px 12px", marginBottom: 2,
              borderRadius: 8, border: "none", cursor: "pointer",
              background: selectedId === p.product_id ? "#eaf3ec" : "transparent",
              fontWeight: selectedId === p.product_id ? 700 : 400,
            }}
          >
            {p.name}
            <div style={{ fontSize: 11, color: p.is_online_active ? GREEN : "#9ca3af", marginTop: 2 }}>
              {p.is_online_active ? "● Live on site" : "○ Not listed"}
            </div>
          </button>
        ))}
        {products.length === 0 && <div style={{ padding: 12, color: "#9ca3af", fontSize: 13 }}>No products yet — map some barcodes first.</div>}
      </div>

      <div style={{ flex: 1 }}>
        {selectedId
          ? <ProductDetail productId={selectedId} onChanged={load} />
          : <div style={{ color: "#9ca3af", padding: 20 }}>Select a product on the left to edit its details, sizes, and images.</div>}
      </div>
    </div>
  );
}

function ProductDetail({ productId, onChanged }) {
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [variantStatusFilter, setVariantStatusFilter] = useState("all");
  const [variantBarcodeFilter, setVariantBarcodeFilter] = useState("");
  const [variantBarcodeFilterDebounced, setVariantBarcodeFilterDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setVariantBarcodeFilterDebounced(variantBarcodeFilter.trim()), 400);
    return () => clearTimeout(t);
  }, [variantBarcodeFilter]);

  const loadAll = useCallback(async () => {
    let stockQuery = supabase
      .from("Stock")
      .select("stock_id, barcode_id, size_label, quantity, status, price, online_price, online_discount, is_online_visible")
      .eq("product_id", productId);

    if (variantStatusFilter !== "all") stockQuery = stockQuery.eq("status", variantStatusFilter);
    if (variantBarcodeFilterDebounced) stockQuery = stockQuery.ilike("barcode_id", `%${variantBarcodeFilterDebounced}%`);

    const [{ data: p }, { data: v }, { data: img }] = await Promise.all([
      supabase.from("products").select("*").eq("product_id", productId).single(),
      stockQuery,
      supabase.from("product_images").select("*").eq("product_id", productId).order("position"),
    ]);
    setProduct(p);
    setVariants(v || []);
    setImages(img || []);
  }, [productId, variantStatusFilter, variantBarcodeFilterDebounced]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function saveProductField(field, value) {
    await supabase.from("products").update({ [field]: value, updated_at: new Date().toISOString() }).eq("product_id", productId);
    onChanged();
  }

  async function toggleLive() {
    const goingLive = !product.is_online_active;
    if (goingLive && images.length === 0) {
      alert("Add at least one image before listing this product online.");
      return;
    }
    if (goingLive) {
      // Check ALL variants regardless of the current status/barcode filter —
      // relying on the (possibly filtered) `variants` list here would wrongly
      // warn "no stock" just because e.g. the filter is set to "sold".
      const { count } = await supabase
        .from("Stock")
        .select("stock_id", { count: "exact", head: true })
        .eq("product_id", productId)
        .gt("quantity", 0);
      if (!count) {
        alert("No size of this product currently has stock — listing it now would show as out of stock everywhere.");
      }
    }
    await supabase.from("products").update({ is_online_active: goingLive }).eq("product_id", productId);
    loadAll();
    onChanged();
  }

  async function saveVariant(stockId, patch) {
    await supabase.from("Stock").update(patch).eq("stock_id", stockId);
    loadAll();
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `${productId}/${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase.storage.from(BUCKET).upload(path, file, { cacheControl: "3600", upsert: false });
    if (uploadError) {
      alert(uploadError.message);
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);
    const { error: insertError } = await supabase.from("product_images").insert({
      product_id: productId,
      storage_path: path,
      url: urlData.publicUrl,
      position: images.length,
    });
    if (insertError) alert(insertError.message);
    setUploading(false);
    e.target.value = "";
    loadAll();
  }

  // Deletes from the storage bucket AND the database row — actually frees space,
  // not just hides the image. Use this for the products you decide not to list.
  async function handleDeleteImage(img) {
    if (!window.confirm("Delete this image? It will be permanently removed from storage.")) return;
    const { error: removeError } = await supabase.storage.from(BUCKET).remove([img.storage_path]);
    if (removeError) return alert(removeError.message);
    await supabase.from("product_images").delete().eq("image_id", img.image_id);
    loadAll();
  }

  if (!product) return null;

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f0f0", padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <input
          defaultValue={product.name}
          onBlur={(e) => saveProductField("name", e.target.value)}
          style={{ ...inputStyle, fontSize: 18, fontWeight: 700, border: "none", padding: "4px 0" }}
        />
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
            <input
              type="checkbox"
              checked={!!product.is_bestseller}
              onChange={(e) => saveProductField("is_bestseller", e.target.checked)}
            />
            Bestseller
          </label>
          <Btn variant={product.is_online_active ? "danger" : "primary"} onClick={toggleLive}>
            {product.is_online_active ? "Unlist from site" : "List on site"}
          </Btn>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 16 }}>
        <Field label="Gender">
          <select defaultValue={product.gender || ""} onChange={(e) => saveProductField("gender", e.target.value || null)} style={inputStyle}>
            <option value="">Select…</option>
            <option value="girls">Girls</option>
            <option value="boys">Boys</option>
            <option value="unisex">Unisex</option>
          </select>
        </Field>
        <Field label="Category">
          <input defaultValue={product.category || ""} onBlur={(e) => saveProductField("category", e.target.value)} style={inputStyle} placeholder="Casual, Ethnic Wear…" />
        </Field>
        <Field label="Subcategory">
          <input defaultValue={product.subcategory || ""} onBlur={(e) => saveProductField("subcategory", e.target.value)} style={inputStyle} placeholder="Sharara, Lehenga…" />
        </Field>
      </div>
      <Field label="Description">
        <textarea defaultValue={product.description || ""} onBlur={(e) => saveProductField("description", e.target.value)} style={{ ...inputStyle, minHeight: 70 }} />
      </Field>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <h4 style={{ margin: 0 }}>Sizes / barcodes</h4>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={variantBarcodeFilter}
            onChange={(e) => setVariantBarcodeFilter(e.target.value)}
            placeholder="Search barcode…"
            style={{ ...inputStyle, width: 150, padding: "6px 10px" }}
          />
          <select value={variantStatusFilter} onChange={(e) => setVariantStatusFilter(e.target.value)} style={{ ...inputStyle, width: 140, padding: "6px 10px" }}>
            <option value="all">All statuses</option>
            <option value="available">Available</option>
            <option value="sold">Sold</option>
            <option value="sold_out">Sold out</option>
            <option value="returned">Returned</option>
          </select>
        </div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", background: "#f9fafb" }}>
            <th style={{ padding: 8 }}>Barcode</th>
            <th style={{ padding: 8 }}>Status</th>
            <th style={{ padding: 8 }}>Size</th>
            <th style={{ padding: 8 }}>Web price (blank = use base)</th>
            <th style={{ padding: 8 }}>Web discount %</th>
            <th style={{ padding: 8 }}>Visible online</th>
          </tr>
        </thead>
        <tbody>
          {variants.map((v) => (
            <tr key={v.stock_id} style={{ borderTop: "1px solid #f3f4f6" }}>
              <td style={{ padding: 8 }}>{v.barcode_id}</td>
              <td style={{ padding: 8 }}><StatusBadge status={v.status} /></td>
              <td style={{ padding: 8 }}>
                <input defaultValue={v.size_label || ""} onBlur={(e) => saveVariant(v.stock_id, { size_label: e.target.value })} style={{ ...inputStyle, padding: "5px 8px" }} />
              </td>
              <td style={{ padding: 8 }}>
                <input type="number" defaultValue={v.online_price ?? ""} placeholder={`₹${v.price}`} onBlur={(e) => saveVariant(v.stock_id, { online_price: e.target.value ? Number(e.target.value) : null })} style={{ ...inputStyle, padding: "5px 8px", width: 100 }} />
              </td>
              <td style={{ padding: 8 }}>
                <input type="number" defaultValue={v.online_discount ?? ""} onBlur={(e) => saveVariant(v.stock_id, { online_discount: e.target.value ? Number(e.target.value) : null })} style={{ ...inputStyle, padding: "5px 8px", width: 80 }} />
              </td>
              <td style={{ padding: 8 }}>
                <input type="checkbox" checked={v.is_online_visible} onChange={(e) => saveVariant(v.stock_id, { is_online_visible: e.target.checked })} />
              </td>
            </tr>
          ))}
          {variants.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 16, textAlign: "center", color: "#9ca3af" }}>
              {variantStatusFilter === "all" && !variantBarcodeFilterDebounced
                ? 'No sizes linked yet — attach barcodes from the "Unmapped Barcodes" tab.'
                : "No barcodes match this filter."}
            </td></tr>
          )}
        </tbody>
      </table>

      <h4 style={{ marginTop: 24, marginBottom: 8 }}>Images</h4>
      <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 0 }}>
        Only upload images for products you're actually listing — each image counts against your Supabase storage. Delete removes it from storage permanently.
      </p>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        {images.map((img) => (
          <div key={img.image_id} style={{ position: "relative", width: 110, height: 110, borderRadius: 10, overflow: "hidden", border: "1px solid #e5e7eb" }}>
            <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button
              onClick={() => handleDeleteImage(img)}
              style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", fontSize: 12 }}
              title="Delete from storage"
            >
              ✕
            </button>
          </div>
        ))}
        <label style={{
          width: 110, height: 110, borderRadius: 10, border: "1px dashed #d1d5db",
          display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#9ca3af", fontSize: 12, textAlign: "center",
        }}>
          {uploading ? "Uploading…" : "+ Add image"}
          <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} style={{ display: "none" }} />
        </label>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// TAB 3 — ORDERS (confirm by phone, then mark packed — that's when stock moves)
// ──────────────────────────────────────────────────────────────────────────
const STATUS_TABS = [
  { id: "pending_confirmation", label: "New" },
  { id: "confirmed", label: "Confirmed" },
  { id: "packed", label: "Packed" },
  { id: "shipped", label: "Shipped" },
  { id: "cancelled", label: "Cancelled" },
];

function OrdersTab() {
  const [filter, setFilter] = useState("pending_confirmation");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    // shipping_addresses embed still works fine — Orders.address_id has a
    // real FK to it already. Only Order_items is fetched separately here,
    // since that's the relationship without a declared FK.
    const { data: ordersData, error: ordersError } = await supabase
      .from("Orders")
      .select(`
        id, order_number, customer_phone, status, grand_total, subtotal, admin_notes, created_at,
        shipping_addresses ( full_name, phone, address_line1, address_line2, city, state, pincode )
      `)
      .eq("source", "online")
      .eq("status", filter)
      .order("created_at", { ascending: false });

    if (ordersError) {
      console.error(ordersError);
      setLoading(false);
      return;
    }

    const orderIds = (ordersData || []).map((o) => o.id);
    let itemsByOrder = {};
    if (orderIds.length > 0) {
      const { data: itemsData, error: itemsError } = await supabase
        .from("Order_items")
        .select("id, order_id, stock_id, product_name, size_label, quantity, price, total")
        .in("order_id", orderIds);
      if (itemsError) console.error(itemsError);
      itemsByOrder = (itemsData || []).reduce((acc, item) => {
        (acc[item.order_id] ||= []).push(item);
        return acc;
      }, {});
    }

    const merged = (ordersData || []).map((o) => ({ ...o, Order_items: itemsByOrder[o.id] || [] }));
    setOrders(merged);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function confirmOrder(id) {
    await supabase.from("Orders").update({ status: "confirmed", updated_at: new Date().toISOString() }).eq("id", id);
    await supabase.from("order_status_history").insert({ order_id: id, status: "confirmed", note: "Confirmed by admin call" });
    load();
  }

  async function cancelOrder(id) {
    const note = window.prompt("Reason for cancelling (optional):") || "";
    const order = orders.find((o) => o.id === id);

    // Restore each item to available — these are unique physical barcodes,
    // and place_online_order marks them 'sold' the moment the order is
    // placed. Cancelling before packing must undo that or the item stays
    // permanently locked out of the shop for no reason.
    const stockIds = (order?.Order_items || []).map((it) => it.stock_id).filter(Boolean);
    if (stockIds.length > 0) {
      await supabase.from("Stock").update({ status: "available" }).in("stock_id", stockIds);
    }

    const combinedNotes = [order?.admin_notes, note ? `Cancelled: ${note}` : "Cancelled"].filter(Boolean).join(" | ");
    await supabase.from("Orders").update({ status: "cancelled", admin_notes: combinedNotes, updated_at: new Date().toISOString() }).eq("id", id);
    await supabase.from("order_status_history").insert({ order_id: id, status: "cancelled", note });
    load();
  }

  async function markPacked(id) {
    if (!window.confirm("Mark packed? This deducts the stock quantity now — same as a physical sale.")) return;
    const { error } = await supabase.rpc("mark_order_packed", { p_order_id: id });
    if (error) return alert(error.message);
    load();
  }

  async function markShipped(id) {
    await supabase.from("Orders").update({ status: "shipped", updated_at: new Date().toISOString() }).eq("id", id);
    await supabase.from("order_status_history").insert({ order_id: id, status: "shipped" });
    load();
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            style={{
              padding: "6px 16px", borderRadius: 50, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
              background: filter === t.id ? GREEN : "#f3f4f6", color: filter === t.id ? "#fff" : "#374151",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ color: "#9ca3af" }}>Loading…</div>}
      {!loading && orders.length === 0 && <div style={{ color: "#9ca3af" }}>No orders in this state.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {orders.map((o) => (
          <div key={o.id} style={{ background: "#fff", border: "1px solid #f0f0f0", borderRadius: 12, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 700 }}>Order #{o.order_number || o.id} · {o.customer_phone}</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <div style={{ fontWeight: 700, color: GREEN }}>₹{o.grand_total}</div>
            </div>

            {o.shipping_addresses && (
              <div style={{ fontSize: 13, color: "#374151", marginTop: 8 }}>
                📍 {o.shipping_addresses.full_name}, {o.shipping_addresses.address_line1}
                {o.shipping_addresses.address_line2 ? `, ${o.shipping_addresses.address_line2}` : ""}, {o.shipping_addresses.city} — {o.shipping_addresses.pincode}
                {" · "}📞 {o.shipping_addresses.phone}
              </div>
            )}

            <div style={{ marginTop: 10, fontSize: 13 }}>
              {o.Order_items?.map((it) => (
                <div key={it.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderTop: "1px dashed #f0f0f0" }}>
                  <span>{it.product_name} {it.size_label ? `(${it.size_label})` : ""} × {it.quantity}</span>
                  <span>₹{it.total}</span>
                </div>
              ))}
            </div>

            {o.admin_notes && (
              <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8, fontStyle: "italic" }}>{o.admin_notes}</p>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {filter === "pending_confirmation" && (
                <>
                  <Btn onClick={() => confirmOrder(o.id)}>Confirm</Btn>
                  <Btn variant="danger" onClick={() => cancelOrder(o.id)}>Cancel</Btn>
                </>
              )}
              {filter === "confirmed" && (
                <>
                  <Btn onClick={() => markPacked(o.id)}>Mark packed</Btn>
                  <Btn variant="danger" onClick={() => cancelOrder(o.id)}>Cancel</Btn>
                </>
              )}
              {filter === "packed" && <Btn onClick={() => markShipped(o.id)}>Mark shipped</Btn>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ROOT
// ──────────────────────────────────────────────────────────────────────────
export default function AdminSales() {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("unmapped");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setChecking(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => listener.subscription.unsubscribe();
  }, []);

  if (checking) return <div style={{ padding: 40 }}>Loading…</div>;
  if (!session) return <AdminLogin />;

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <header style={{ background: "#1a2e1a", color: "#fff", padding: "16px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: 700, fontSize: 18 }}>Ritu Fashion Store — Admin</div>
        <button onClick={() => supabase.auth.signOut()} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 8, padding: "8px 16px", cursor: "pointer" }}>
          Sign out
        </button>
      </header>

      <nav style={{ display: "flex", gap: 8, padding: "16px 32px", background: "#fff", borderBottom: "1px solid #e5e7eb" }}>
        {[
          { id: "unmapped", label: "Unmapped Barcodes" },
          { id: "products", label: "Products" },
          { id: "orders", label: "Orders" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "8px 18px", borderRadius: 50, border: "none", cursor: "pointer", fontWeight: 600,
              background: tab === t.id ? GREEN : "#f3f4f6", color: tab === t.id ? "#fff" : "#374151",
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main style={{ padding: 32 }}>
        {tab === "unmapped" && <UnmappedBarcodesTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
      </main>
    </div>
  );
}